import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Alert,
  Chip,
  Button,
  Tooltip,
  useMediaQuery,
  Card,
  CardContent,
  Divider,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import axios from "axios";
import baseURL from "../../config";

const UploadedCVsList = () => {
  const [cvs, setCvs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  useEffect(() => {
    const fetchCVs = async () => {
      try {
        setLoading(true);
        const uploadedById = localStorage.getItem("user id");
        if (!uploadedById) {
          setError("User ID not found in local storage.");
          setLoading(false);
          return;
        }

        const res = await axios.get(
          `${baseURL}/api/crewCVuploadByCrewingAgentRoute/uploaded/${uploadedById}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          }
        );

        const flatList = res.data.data.flatMap((item) =>
          item.cvs.map((cv) => ({
            ...cv,
            parentId: item._id,
            isApproved: item.isApproved,
          }))
        );

        setCvs(flatList);
      } catch (error) {
        console.error("Error fetching uploaded CVs:", error);
        setError("Failed to load CVs.");
      } finally {
        setLoading(false);
      }
    };

    fetchCVs();
  }, []);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress size={45} thickness={4} />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 2, fontSize: "1rem" }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box sx={{ mt: 3 }}>
      <Typography
        variant="h5"
        gutterBottom
        sx={{ fontWeight: "bold", color: "red" }}
      >
        📄 Uploaded CVs
      </Typography>

      {cvs.length === 0 ? (
        <Alert severity="info" sx={{ fontSize: "1rem" }}>
          No CVs found for this user.
        </Alert>
      ) : isMobile ? (
        // 📱 Mobile Card View
        <Box>
          {cvs.map((cv, index) => (
            <Card
              key={index}
              sx={{
                mb: 2,
                boxShadow: 3,
                borderRadius: 2,
                "&:hover": { boxShadow: 6 },
              }}
            >
              <CardContent>
                <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                  {cv.name} ({cv.rank})
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary" }}>
                  Uploaded:{" "}
                  {new Date(cv.uploadedAt).toLocaleDateString("en-IN", {
                    day: "2-digit",
                    month: "short",
                    year: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
                <Divider sx={{ my: 1 }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    flexWrap: "wrap",
                    gap: 1,
                  }}
                >
                  <Tooltip title="View CV">
                    <Button
                      variant="contained"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      sx={{
                        backgroundColor: "red",
                        textTransform: "none",
                        "&:hover": { backgroundColor: "red" },
                      }}
                      href={`${baseURL}/${cv.filePath}`}
                      target="_blank"
                    >
                      View
                    </Button>
                  </Tooltip>
                  <Chip
                    icon={
                      cv.isApproved ? <CheckCircleIcon /> : <HourglassEmptyIcon />
                    }
                    label={cv.isApproved ? "Approved" : "Pending"}
                    color={cv.isApproved ? "success" : "warning"}
                    variant="filled"
                    sx={{
                      fontWeight: "bold",
                      textTransform: "uppercase",
                    }}
                  />
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      ) : (
        // 💻 Desktop Table View
        <TableContainer
          component={Paper}
          sx={{
            borderRadius: 3,
            overflow: "hidden",
            boxShadow: 4,
          }}
        >
          <Table>
            <TableHead sx={{ backgroundColor: "red" }}>
              <TableRow>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>#</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Name</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Rank</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>File</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Uploaded</TableCell>
                <TableCell sx={{ color: "white", fontWeight: "bold" }}>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {cvs.map((cv, index) => (
                <TableRow
                  key={index}
                  sx={{
                    backgroundColor: index % 2 === 0 ? "#f4f6f8" : "white",
                    "&:hover": { backgroundColor: "#e3f2fd" },
                  }}
                >
                  <TableCell>{index + 1}</TableCell>
                  <TableCell sx={{ fontWeight: "500" }}>{cv.name}</TableCell>
                  <TableCell>{cv.rank}</TableCell>
                  <TableCell>
                    <Tooltip title="View CV">
                      <Button
                        variant="contained"
                        size="small"
                        startIcon={<VisibilityIcon />}
                        sx={{
                          backgroundColor: "red",
                          textTransform: "none",
                          "&:hover": { backgroundColor: "darkred" },
                        }}
                        href={`${baseURL}/${cv.filePath}`}
                        target="_blank"
                      >
                        View
                      </Button>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    {new Date(cv.uploadedAt).toLocaleDateString("en-IN", {
                      day: "2-digit",
                      month: "short",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </TableCell>
                  <TableCell>
                    <Chip
                      icon={
                        cv.isApproved ? (
                          <CheckCircleIcon />
                        ) : (
                          <HourglassEmptyIcon />
                        )
                      }
                      label={cv.isApproved ? "Approved" : "Pending"}
                      color={cv.isApproved ? "success" : "warning"}
                      variant="filled"
                      sx={{
                        fontWeight: "bold",
                        textTransform: "uppercase",
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default UploadedCVsList;
